// Shared South African bank list and account number validation rules.
// Used by both PageOne.jsx (Bank Account Details for Tax Refund - Page 1 baseline)
// and PageSeven.jsx (Debit Order Mandate) so both pages apply identical rules.

export const SOUTH_AFRICAN_BANKS = [
  { value: 'absa', label: 'ABSA Bank', branchCode: '632005' },
  { value: 'african-bank', label: 'African Bank', branchCode: '430000' },
  { value: 'albaraka-bank', label: 'Albaraka Bank', branchCode: '800000' },
  { value: 'bank-zero', label: 'Bank Zero', branchCode: '888000' },
  { value: 'barclays-bank', label: 'Barclays Bank', branchCode: '590000' },
  { value: 'bidvest-bank', label: 'Bidvest Bank', branchCode: '462005' },
  { value: 'capitec', label: 'Capitec Bank', branchCode: '470010' },
  { value: 'capitec-business', label: 'Capitec Business Bank', branchCode: '450105' },
  { value: 'discovery-bank', label: 'Discovery Bank', branchCode: '679000' },
  { value: 'fnb', label: 'First National Bank (FNB)', branchCode: '250655' },
  { value: 'firstrand', label: 'FirstRand Bank Ltd', branchCode: '201419' },
  { value: 'grindrod-bank', label: 'Grindrod Bank', branchCode: '584000' },
  { value: 'habib-bank', label: 'Habib Overseas Bank', branchCode: '170102' },
  { value: 'hbz-bank', label: 'HBZ Bank', branchCode: '570105' },
  { value: 'hollard-bank', label: 'Hollard Bank', branchCode: '585001' },
  { value: 'hsbc-bank', label: 'HSBC Bank', branchCode: '587000' },
  { value: 'investec', label: 'Investec Bank', branchCode: '580105' },
  { value: 'ithala-bank', label: 'Ithala Bank', branchCode: '420000' },
  { value: 'mercantile-bank', label: 'Mercantile Bank (now Capitec Business Bank)', branchCode: '450105' },
  { value: 'nedbank', label: 'Nedbank', branchCode: '198765' },
  { value: 'old-mutual-bank', label: 'Old Mutual Bank', branchCode: '462005' },
  { value: 'postbank', label: 'PostBank (South African Post Office)', branchCode: '460005' },
  { value: 'rmb', label: 'Rand Merchant Bank', branchCode: '261251' },
  { value: 'rmbpb', label: 'RMB Private Bank', branchCode: '222026' },
  { value: 'sasfin-bank', label: 'Sasfin Bank', branchCode: '683000' },
  { value: 'bank-of-athens', label: 'South African Bank of Athens Limited', branchCode: '410506' },
  { value: 'standard-bank', label: 'Standard Bank', branchCode: '051001' },
  { value: 'standard-chartered', label: 'Standard Chartered Bank', branchCode: '730020' },
  { value: 'tymebank', label: 'TymeBank', branchCode: '678910' },
  { value: 'ubank', label: 'Ubank', branchCode: '431010' },
].sort((a, b) => a.label.localeCompare(b.label));

export const validateAccountNumber = (accountNumber, bankName) => {
  if (!accountNumber || !bankName) return '';

  // Data Sanitization: Remove all non-numeric characters
  const cleanNumber = accountNumber.replace(/[^0-9]/g, '');

  // Pattern validation - reject obviously invalid patterns
  if (/^(.)\1+$/.test(cleanNumber)) return 'Account number appears invalid. Please enter a valid bank account number.';
  if (/^0+$/.test(cleanNumber)) return 'Account number appears invalid. Please enter a valid bank account number.';

  const hasAscendingSequence = (str) => {
    for (let i = 0; i < str.length - 5; i++) {
      let isSequential = true;
      for (let j = 0; j < 6; j++) {
        if (parseInt(str[i + j]) !== (parseInt(str[i]) + j) % 10) { isSequential = false; break; }
      }
      if (isSequential) return true;
    }
    return false;
  };
  if (hasAscendingSequence(cleanNumber)) return 'Account number appears invalid. Sequential numbers are not permitted.';

  if (/(\d)\1{3,}/.test(cleanNumber)) return 'Account number appears unusual. Contains four or more consecutive identical digits. Please verify before proceeding.';

  const uniqueDigits = new Set(cleanNumber.split('')).size;
  if (uniqueDigits < 3) return 'Account number appears invalid. Please enter a valid bank account number with varied digits.';

  // Bank-specific validation rules based on deep research
  // Reference: PASA specifications, bank documentation, and CDV requirements
  const bankValidation = {
    // ========== THE "BIG FOUR" LEGACY BANKS ==========
    'ABSA Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: ['1', '4', '9'],
      prefixRequired: false, // Due to merger history, prefixes are common but not mandatory
      description: '10 or 11 digits',
      note: 'Accounts are left-padded with zeros to 11 digits for CDV processing.'
    },
    'First National Bank (FNB)': {
      minLength: 11,
      maxLength: 11,
      prefixes: ['5', '6', '7'],
      prefixRequired: true,
      description: '11 digits',
      note: 'Cheque accounts start with 6, Savings with 7 or 5.'
    },
    'Nedbank': {
      minLength: 10,
      maxLength: 10,
      prefixes: ['1'],
      prefixRequired: true,
      description: '10 digits',
      note: 'Please ensure you enter your Account Number, not your Profile Number.'
    },
    'Standard Bank': {
      minLength: 9,
      maxLength: 11,
      prefixes: ['0', '1', '2', '3', '4'],
      prefixRequired: false,
      description: '9 to 11 digits',
      note: '9-digit accounts are valid and will be left-padded with zeros for processing.'
    },

    // ========== MASS MARKET CHALLENGERS ==========
    'Capitec Bank': {
      minLength: 10,
      maxLength: 10,
      prefixes: ['1', '2'],
      prefixRequired: true,
      description: '10 digits',
      note: 'Savings accounts start with 1, Loan/Bond accounts start with 2.'
    },
    'Capitec Business Bank': {
      minLength: 10,
      maxLength: 10,
      prefixes: ['1', '2'],
      prefixRequired: false,
      description: '10 digits'
    },
    'African Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Ubank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits',
      note: 'Formerly Teba Bank.'
    },

    // ========== DIGITAL & NICHE BANKS ==========
    'Discovery Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits',
      rejectLength: 16, // Reject card numbers
      note: 'Please enter your account number, not your 16-digit card number.'
    },
    'TymeBank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Bank Zero': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
    'Bidvest Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Sasfin Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Investec Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: ['1', '5'],
      prefixRequired: false,
      description: '11 digits',
      note: 'Private Bank accounts often start with 1, Corporate with 5.'
    },

    // ========== SMALLER & FOREIGN-DOMICILED BANKS ==========
    'South African Bank of Athens Limited': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits',
      note: 'Formerly Access Bank South Africa.'
    },
    'Albaraka Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits',
      note: 'Islamic Banking. Do not enter international IBAN format.'
    },
    'Grindrod Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Habib Overseas Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'HBZ Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Ithala Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Mercantile Bank (now Capitec Business Bank)': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'PostBank (South African Post Office)': {
      minLength: 11,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '11 digits'
    },
    'Rand Merchant Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: ['5', '6', '7'],
      prefixRequired: false,
      description: '11 digits',
      note: 'Uses same validation rules as FNB.'
    },
    'RMB Private Bank': {
      minLength: 11,
      maxLength: 11,
      prefixes: ['5', '6', '7'],
      prefixRequired: false,
      description: '11 digits',
      note: 'Uses same validation rules as FNB.'
    },

    // ========== OTHER BANKS (Added for completeness) ==========
    'Barclays Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
    'FirstRand Bank Ltd': {
      minLength: 11,
      maxLength: 11,
      prefixes: ['5', '6', '7'],
      prefixRequired: false,
      description: '11 digits',
      note: 'Parent company of FNB - uses same validation rules.'
    },
    'Hollard Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
    'HSBC Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
    'Old Mutual Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
    'Standard Chartered Bank': {
      minLength: 10,
      maxLength: 11,
      prefixes: null,
      prefixRequired: false,
      description: '10 or 11 digits'
    },
  };

  // Find matching bank validation rules
  const bankKey = Object.keys(bankValidation).find(key =>
    bankName.toLowerCase() === key.toLowerCase() ||
    bankName.toLowerCase().includes(key.toLowerCase()) ||
    key.toLowerCase().includes(bankName.toLowerCase())
  );

  if (bankKey) {
    const validation = bankValidation[bankKey];
    const { minLength, maxLength, prefixes, prefixRequired, description, rejectLength, note } = validation;

    // Special check: Reject specific lengths (e.g., Discovery Bank 16-digit card numbers)
    if (rejectLength && cleanNumber.length === rejectLength) {
      return `${note || `Please enter a valid ${description} account number, not a ${rejectLength}-digit number.`}`;
    }

    // Length validation
    if (cleanNumber.length < minLength || cleanNumber.length > maxLength) {
      let errorMsg = minLength === maxLength
        ? `${bankKey} account numbers must be exactly ${minLength} digits. You entered ${cleanNumber.length} digits.`
        : `${bankKey} account numbers must be ${description}. You entered ${cleanNumber.length} digits.`;

      if (note) errorMsg += ` Note: ${note}`;
      return errorMsg;
    }

    // Prefix validation (only if prefixes are defined and required)
    if (prefixes && prefixRequired && cleanNumber.length > 0) {
      const firstDigit = cleanNumber.charAt(0);
      if (!prefixes.includes(firstDigit)) {
        return `${bankKey} account numbers typically start with ${prefixes.join(', ')}. Your account starts with ${firstDigit}. Please verify the account number is correct.`;
      }
    }
  } else {
    // Default validation for any unlisted banks (SA standard range: 9-11 digits)
    if (cleanNumber.length < 9 || cleanNumber.length > 11) {
      return `South African bank account numbers are typically 9-11 digits. You entered ${cleanNumber.length} digits. Please verify.`;
    }
  }

  return '';
};
